import { ModalWindow } from '@cian/ui-kit';
import { useBooleanState } from '@cian/valuation-utils-component';
import * as React from 'react';
import { useDispatch, useSelector, batch } from 'react-redux';

import { getOffersHistory, resetFilters, resetListingData, setIsFiltersChanged } from '../../../actions/offersHistory';
import { EntryBanner } from '../../../components/OffersHistory/Mobile';
import { selectOffersHistoryData, selectOffersHistoryExists } from '../../../selectors/offersHistory';
import { TThunkDispatch } from '../../../types/redux';
import { OffersHistoryContentLoadable } from './OffersHistoryContentLoadable';
import { trackEntryBannerIntoView, trackOffersHistoryListingClick } from './analytics';
import { TRefWrapper } from '../../Scrolling';
import { RefWrapper } from '../../../components/packages/RefWrapper';
import { useTitle } from './utils/useTitle';

interface IOffersHistoryContainerProps {
  refWrapper: TRefWrapper;
}

export const OffersHistoryContainer: React.FC<IOffersHistoryContainerProps> = ({ refWrapper }) => {
  const [isOpen, onHandleOpen, onHandleClose] = useBooleanState();
  const dispatch = useDispatch<TThunkDispatch>();
  const { data: offersHistoryData } = useSelector(selectOffersHistoryData);
  const offersHistoryExists = useSelector(selectOffersHistoryExists);
  const entryTexts = useTitle();

  React.useEffect(() => {
    if (isOpen && !offersHistoryData) {
      dispatch(getOffersHistory(false));
    }

    return () => {
      dispatch(setIsFiltersChanged(false));
    };
  }, [dispatch, isOpen, offersHistoryData]);

  const handleEntryBannerClick = React.useCallback(() => {
    onHandleOpen();
    trackOffersHistoryListingClick();
  }, [onHandleOpen]);

  const onModalCloseHandler = React.useCallback(() => {
    /** При закрытии модалки, фильтры, которые накликал юзер, и данные листинга сбросятся */
    batch(() => {
      dispatch(resetFilters());
      dispatch(resetListingData());
    });
    onHandleClose();
  }, [dispatch, onHandleClose]);

  if (!offersHistoryExists) {
    return null;
  }

  return refWrapper(
    <RefWrapper>
      <EntryBanner entryTexts={entryTexts} onView={trackEntryBannerIntoView} onClick={handleEntryBannerClick} />
      <ModalWindow
        open={isOpen}
        onClose={onModalCloseHandler}
        title="История объявлений"
        content={<OffersHistoryContentLoadable />}
      />
    </RefWrapper>,
  );
};
